import {Component, Expressions} from '@packages/systems/wfdl';
import {
  getPluginName,
  namesEqual,
  pluginName,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {Err, Ok, type Result} from '@packages/utilities/fp/result';
import {
  CAMPAIGN_COMPONENT_PLACEMENT_KIND,
  getCampaignComponentGroupRule,
  type CampaignComponentKind,
} from '@packages/domains/campaigns/utilities';

const componentOnlyDropTargetType = qualify(
  pluginName('PageBuilding'),
  Component.name('DropTarget')
);

function isComponentOnlyDropTarget(element: ExpressionElement): boolean {
  return namesEqual(element.type, componentOnlyDropTargetType);
}

function isUserComponentInst(
  components: Component.ComponentMap,
  element: ExpressionElement
): boolean {
  return (
    getPluginName(element.type) === '__SitePlugin' &&
    components.has(element.type)
  );
}

function getCampaignComponentKindFromGroupName(
  groupName: string
): CampaignComponentKind {
  return getCampaignComponentGroupRule(groupName)?.placementKind ?? null;
}

export function getCampaignComponentKind(
  componentMap: Component.ComponentMap,
  element: ExpressionElement
): CampaignComponentKind {
  const componentDef = componentMap.get(element.type);
  if (!componentDef) return null;
  const groupName = Component.getGroupName(componentDef);
  const slashIdx = groupName.indexOf('/');
  const topLevelGroupName =
    slashIdx < 0 ? groupName : groupName.slice(0, slashIdx);

  return getCampaignComponentKindFromGroupName(topLevelGroupName);
}

/**
 * The type check matters: an in-place replacement keeps the element id while
 * swapping the component, so an id-only match would let the replacement
 * inherit the original's placement exemption.
 */
function isUnchangedChildOf(
  render: Expression | undefined,
  child: ExpressionElement,
  parentId: string
): boolean {
  if (render === undefined) {
    return false;
  }

  const liveChild = Expressions.getElementById(render, child.id);

  return (
    liveChild !== null &&
    namesEqual(liveChild.type, child.type) &&
    Expressions.getParent(render, child.id)?.id === parentId
  );
}

function isInsideComponentInstance(
  stagedComponentMap: Component.ComponentMap,
  pageRender: Expression | undefined,
  rawParent: ExpressionElement
): boolean {
  if (!pageRender) {
    return true;
  }

  const closestComponentInst = Expressions.closestElement(
    pageRender,
    rawParent.id,
    (el) => isUserComponentInst(stagedComponentMap, el)
  );

  return Boolean(closestComponentInst);
}

export function validateCampaignBuildModePlacement({
  stagedComponentMap,
  rawParent,
  rawChild,
  pageRender,
  livePageRender,
}: {
  stagedComponentMap: Component.ComponentMap;
  rawParent: ExpressionElement;
  rawChild: ExpressionElement;
  pageRender: Expression | undefined;
  livePageRender: Expression | undefined;
}): Result<string, string> {
  const childCampaignKind = getCampaignComponentKind(
    stagedComponentMap,
    rawChild
  );
  const parentIsPageSlot = isComponentOnlyDropTarget(rawParent);

  // A component already in this Page Slot stays reorderable there, whatever its
  // group -- including Element group components, whose rule below is skipped too.
  if (
    parentIsPageSlot &&
    isUnchangedChildOf(livePageRender, rawChild, rawParent.id)
  ) {
    return Ok('ok');
  }

  if (
    childCampaignKind === CAMPAIGN_COMPONENT_PLACEMENT_KIND.ELEMENT &&
    (parentIsPageSlot ||
      !isInsideComponentInstance(stagedComponentMap, pageRender, rawParent))
  ) {
    return Err('Elements can only be added inside a Component Slot');
  }

  if (
    parentIsPageSlot &&
    childCampaignKind !== CAMPAIGN_COMPONENT_PLACEMENT_KIND.SECTION
  ) {
    return Err('Only Section components can be placed directly on the page');
  }

  if (
    childCampaignKind === CAMPAIGN_COMPONENT_PLACEMENT_KIND.SECTION &&
    !parentIsPageSlot
  ) {
    return Err('Sections must be placed directly on the page');
  }

  return Ok('ok');
}
